/* eslint-disable no-nested-ternary */
import { useMemo } from 'react'
import { Grid } from '@mui/material'
import { useNetInfo, useStyles } from '@platform/react/hook'
import Button from 'ui/Element/Button'
import Text from 'ui/Element/Text'
import Icon from 'ui/Element/Icon'

export const styles = (theme, { large, fullHeight, fill }) => ({
  root: {
    gap: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: fill
      ? '100%'
    // Roughly. Should be 100vh - header height
    // Defaults to 70
      : fullHeight
        ? '70vh'
        : '22vh',
  },
  button: {
    borderRadius: '0.5rem',
    boxShadow: 'none',
    fontWeight: 700,
    fontSize: '0.875rem',
    height: '3rem',
  },
  ...theme.custom.noData,
})

/**
 * @typedef {'outlined'|'filled'} iconVariants
 */

/**
 * NoData Component
 *
 * @param {Object} p                          data for the component
 * @param {string |{ name:string }} p.icon    icon to show at the top
 * @param {string} p.label                    label to display
 * @param {string} p.description              description to display below the `label`
 *
 * @param {string} p.offlineIcon              icon to show at the top when offline
 * @param {string} p.offlineLabel             label to display when offline
 * @param {string} p.offlineDescription       description to display below the `label`
 *                                            when the application is offline
 *
 * @param {string} p.searchIcon               icon to show at the top when a search was executed
 * @param {string} p.searchLabel              label to display when a search was executed
 * @param {string} p.searchDescription        description to display below the `label`
 *                                            when a search was executed
 *
 * @param {Boolean} p.action                  whether or not to display the button
 * @param {Boolean} p.large                   whether or not to use large texts
 * @param {Boolean} [p.fullHeight=true]       whether to use the full height or not.
 * @param {Boolean} [p.fill=false]            whether it should fill the parent
 *                                            container instead of having a fixed height
 *                                            should be set to `false` in short lists
 * @param {Gaia.Component.Events} p.events    events for the component
 * @param {string} p.buttonIcon               icon to show inside the action button
 * @param {string} p.buttonLabel              label for the button
 * @param {string} p.buttonColor              label for the button
 * @param {boolean} p.showLabel               whether to show the label
 * @param {boolean} [p.showIcon=true]         whether to show the icon
 * @param {boolean} [p.showButtonIcon=true]   whether to show the button icon
 * @param {?number[]} [p.aclProps]            list of permissions to check against
 * @param {boolean} [p.showDescription=true]  whether or not to display the description
 * @param {boolean} [p.fromSearch=false]      whether we come from an executed search
 * @param {Object} ref                        reference to the root element
 *
 * @returns {JSX.Element}
 */
const NoData = ({
  label,
  description,
  searchLabel,
  searchDescription,
  icon,
  searchIcon,
  offlineLabel,
  offlineDescription,
  offlineIcon,
  buttonLabel,
  buttonIcon,
  showIcon = true,
  showButtonIcon = true,
  showLabel = false,
  showDescription = true,
  buttonColor = 'primary',
  fullHeight = true,
  fill = false,
  large = false,
  action = true,
  fromSearch = false,
  events,
  ...props
}, ref) => {
  const [online] = useNetInfo()
  const classes = useStyles(styles, { large, fullHeight, fill })()
  const hasPermission = useMemo(() => events?.acl?.(props.aclProps || null) || false, [events])

  const labelText = !online ? offlineLabel : fromSearch ? searchLabel : label
  const descriptionText = !online ? offlineDescription : fromSearch ? searchDescription : description
  const iconValue = !online ? offlineIcon : fromSearch ? searchIcon : icon.name ?? icon

  return (
    <Grid
      ref={ref}

      item
      container
      className={classes.root}
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
    >
      {showIcon && (
        <Grid
          item
          ms={'auto'}
          component={Icon}

          size={64}
          name={iconValue}
        />
      )}
      {showLabel && (
        <Grid
          item
          ms={'auto'}
          component={Text}

          align={'center'}
          variant={'16/bold'}
          color={'black.main'}
        >
          {labelText}
        </Grid>
      )}
      {showDescription && (
        <Grid
          item
          ms={'auto'}
          style={{ maxWidth: '70%' }}
        >
          <Text
            align={'center'}
            color={'black.main'}
            variant={'14/medium'}
            value={descriptionText}
          />
        </Grid>
      )}
      {action && online && hasPermission && (
        <Grid
          item
          ms={'auto'}
        >
          <Button
            events={events}
            fullWidth={false}
            value={buttonLabel}
            color={buttonColor}
            variant={'contained'}
            className={classes.button}
            startIcon={showButtonIcon ? buttonIcon : null}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default NoData
